import { useEffect, useRef, useState } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

import { useSimpleRef } from '../util/hooks';

/**
 * Feature detection for upload page drag-and-drop.
 */
export function canDragAndDrop() {
  const dummyDiv = document.createElement('div');
  return (
    ('draggable' in dummyDiv || ('ondragstart' in dummyDiv && 'ondrop' in dummyDiv)) &&
    'FormData' in window &&
    'FileReader' in window
  );
}

export function useMeasure<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T>();
  const [bounds, setBounds] = useState({ left: 0, top: 0, width: 0, height: 0 });

  const { current: ro } = useSimpleRef(
    () =>
      new ResizeObserver(([entry]: ResizeObserverEntry[]): void => {
        setBounds(entry.contentRect);
      }),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: ro is a stable reference
  useEffect(() => {
    return () => {
      ro.disconnect();
    };
  }, []);

  return [
    (el: T) => {
      // Only run this once by waiting for element and not running again
      // when we already have the ref
      if (!ref.current) {
        ref.current = el;
        // Start ResizeObserver
        ro.observe(el);
      }
    },
    bounds,
  ] as const;
}

export async function injectScript(props: {
  src: string;
  id?: string;
  async?: boolean;
  defer?: boolean;
  type?: 'module';
  crossOrigin?: 'anonymous' | string;
}) {
  return new Promise((resolve, reject) => {
    const scriptEl = document.createElement('script');
    if (props.async) {
      scriptEl.async = props.async;
    }
    if (props.defer) {
      scriptEl.defer = props.defer;
    }
    if (props.crossOrigin) {
      scriptEl.crossOrigin = props.crossOrigin;
    }
    if (props.type) {
      scriptEl.type = props.type;
    }
    if (props.id) {
      scriptEl.id = props.id;
    }
    scriptEl.src = props.src;
    scriptEl.addEventListener('load', resolve);
    scriptEl.addEventListener('error', reject);
    document.body.appendChild(scriptEl);
  });
}

export async function injectLink(props: { rel: 'stylesheet'; href: string }) {
  return new Promise((resolve, reject) => {
    const linkEl = document.createElement('link');
    linkEl.rel = props.rel;
    linkEl.href = props.href;
    linkEl.addEventListener('load', resolve);
    linkEl.addEventListener('error', reject);
    document.body.appendChild(linkEl);
  });
}

export function observeElementInViewport(elm: HTMLElement, callback: (isVisible: boolean) => void) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        callback(entry.intersectionRatio > 0);
      });
    },
    { threshold: 0 },
  );

  observer.observe(elm);

  return () => {
    observer.disconnect();
  };
}

export const waitForAnimation = (el: Element | null, type: 'transition' | 'animation' = 'animation'): Promise<void> => {
  if (!el) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const _end = () => {
      el.removeEventListener(`${type}end`, _end);
      resolve();
    };

    el.addEventListener(`${type}end`, _end);
  });
};

export function triggerFileSelector({
  allowMultiple,
  callback,
  acceptTypes,
}: {
  allowMultiple?: boolean;
  acceptTypes?: string;
  callback: (files: File[]) => void;
}) {
  const fileSelector = document.createElement('input');
  fileSelector.setAttribute('type', 'file');

  if (acceptTypes) {
    fileSelector.setAttribute('accept', acceptTypes);
  }

  if (allowMultiple) {
    fileSelector.setAttribute('multiple', 'multiple');
  }

  fileSelector.addEventListener('change', () => {
    callback(Array.from(fileSelector.files ?? []));
  });

  fileSelector.click();

  return fileSelector;
}
